import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '@/api/services';
import { AccountOperationType, InterfaceResponseEnum } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import naLogo from '@/assets/Nautic Alert Logo.png';

type LookupMethod = 'phone' | 'email';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS_ONLY = /^\d+$/;

export function ForgotUsernamePage() {
  const [method, setMethod] = useState<LookupMethod>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-slate-500 dark:focus:border-teal-500';
  const labelClass =
    'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (method === 'phone') {
      const digits = phone.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 20 || !DIGITS_ONLY.test(digits)) {
        setError('Please enter a valid phone number (10-20 digits).');
        return;
      }
    } else {
      if (!EMAIL_REGEX.test(email) || email.length > 50) {
        setError('Please enter a valid email address (max 50 characters).');
        return;
      }
    }

    setIsLoading(true);
    try {
      const resp = await authService.accountOperation({
        operationType: AccountOperationType.RECOVER_USER_ID,
        ...(method === 'phone'
          ? { cellStr: phone.replace(/\D/g, '') }
          : { emailStr: email }),
      });

      const flat = resp as unknown as { httpStatusCode: number; responseEnum: number };
      const statusCode = resp.responseObj?.httpStatusCode ?? flat.httpStatusCode ?? 400;
      const enumVal = Number(resp.responseObj?.responseEnum ?? flat.responseEnum);

      // Server returns 200 even when not found for security
      if (
        statusCode === 200 ||
        enumVal === InterfaceResponseEnum.NOT_FOUND ||
        enumVal === InterfaceResponseEnum.NO_ENTRIES
      ) {
        setSuccess(true);
      } else if (enumVal === InterfaceResponseEnum.CELL_INVALID) {
        setError('Invalid phone number format.');
      } else if (enumVal === InterfaceResponseEnum.EMAIL_INVALID) {
        setError('Invalid email address format.');
      } else if (enumVal === InterfaceResponseEnum.SERVER_ERROR) {
        setError('Could not send recovery email. Please try again later.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-navy-950">
      <div className="w-full max-w-md px-4">
        {/* Logo & Brand */}
        <div className="mb-8 text-center">
          <img src={naLogo} alt="Nautic Alert" className="mx-auto mb-4 h-16 object-contain" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Vessel Monitoring Portal</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-lg dark:border-navy-700 dark:bg-navy-900">
          {success ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <svg
                  className="h-7 w-7 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                Recovery Sent
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                If an account exists with that {method === 'phone' ? 'phone number' : 'email address'}, your username has been sent.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm font-medium text-teal-600 hover:text-teal-500 dark:text-teal-400 dark:hover:text-teal-300"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <h2 className="mb-4 text-center text-lg font-semibold text-slate-800 dark:text-white">
                Find Your Username
              </h2>

              {/* Method toggle */}
              <div className="mb-5 flex rounded-lg bg-slate-100 p-1 dark:bg-navy-800">
                <button
                  type="button"
                  onClick={() => { setMethod('phone'); setError(''); }}
                  className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                    method === 'phone'
                      ? 'bg-white text-slate-900 shadow-sm dark:bg-navy-700 dark:text-white'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                  }`}
                >
                  Phone Number
                </button>
                <button
                  type="button"
                  onClick={() => { setMethod('email'); setError(''); }}
                  className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                    method === 'email'
                      ? 'bg-white text-slate-900 shadow-sm dark:bg-navy-700 dark:text-white'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                  }`}
                >
                  Email Address
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                    {error}
                  </div>
                )}

                {method === 'phone' ? (
                  <div>
                    <label htmlFor="fu-phone" className={labelClass}>
                      Phone Number
                    </label>
                    <input
                      id="fu-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={inputClass}
                      placeholder="Enter your phone number"
                      autoComplete="tel"
                      autoFocus
                    />
                  </div>
                ) : (
                  <div>
                    <label htmlFor="fu-email" className={labelClass}>
                      Email Address
                    </label>
                    <input
                      id="fu-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      placeholder="you@example.com"
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || (method === 'phone' ? !phone : !email)}
                  loading={isLoading}
                  className="w-full"
                >
                  Recover Username
                </Button>
              </form>

              <div className="mt-6 border-t border-slate-200 pt-6 text-center dark:border-navy-700">
                <Link
                  to="/login"
                  className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                >
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          &copy; {new Date().getFullYear()} Nautic Alert. All rights reserved.
        </p>
      </div>
    </div>
  );
}
